import React, { useState } from 'react';

const PhotoGallery = ({ mainImage, images = [] }) => {
  const [showModal, setShowModal] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const allImages = images.length > 0 ? images : [{ imageUrl: mainImage }];

  const openModal = (index = 0) => {
    setCurrentImageIndex(index);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
  };

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % allImages.length);
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + allImages.length) % allImages.length);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowRight') nextImage();
    if (e.key === 'ArrowLeft') prevImage();
    if (e.key === 'Escape') closeModal();
  };

  return (
    <div className="relative">
      {/* Enhanced Gallery Layout */}
      <div className="relative overflow-hidden">
        <div className="grid grid-cols-5 gap-2 h-80">
          {/* Main Large Image - Takes 3 columns */}
          <div className="col-span-3 relative group overflow-hidden rounded-l-2xl">
            <img
              src={mainImage || 'https://via.placeholder.com/600x400?text=Hotel+Image'}
              alt="Main Hotel Image"
              className="w-full h-full object-cover cursor-pointer transition-all duration-500 group-hover:scale-110"
              onClick={() => openModal(0)}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-all duration-300">
              <div className="absolute bottom-4 left-4 text-white">
                <div className="bg-white/20 backdrop-blur-sm rounded-lg px-3 py-2">
                  <div className="flex items-center space-x-2">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                    <span className="text-sm font-medium">View Gallery</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Grid of Smaller Images - Takes 2 columns */}
          <div className="col-span-2 grid grid-cols-2 gap-2">
            {allImages.slice(1, 5).map((image, index) => (
              <div key={index} className={`relative group overflow-hidden ${
                index === 1 ? 'rounded-tr-2xl' : 
                index === 3 ? 'rounded-br-2xl' : ''
              }`}>
                <img
                  src={image.imageUrl || 'https://via.placeholder.com/200x150?text=Hotel'}
                  alt={`Hotel Image ${index + 2}`}
                  className="w-full h-full object-cover cursor-pointer transition-all duration-500 group-hover:scale-110"
                  onClick={() => openModal(index + 1)}
                />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all duration-300" />
                
                {/* Show count overlay on last image if there are more */}
                {index === 3 && allImages.length > 5 && (
                  <div 
                    className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent flex items-center justify-center cursor-pointer"
                    onClick={() => openModal(index + 1)}
                  >
                    <div className="text-center text-white">
                      <div className="text-2xl font-bold">+{allImages.length - 5}</div>
                      <div className="text-sm font-medium">more photos</div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Enhanced View All Photos Button */}
        <div className="absolute bottom-4 right-4">
          <button
            onClick={() => openModal(0)}
            className="bg-white/90 backdrop-blur-sm text-gray-900 px-4 py-2 rounded-lg font-medium hover:bg-white transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 flex items-center space-x-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            <span>View All {allImages.length} Photos</span>
          </button>
        </div>
      </div>

      {/* Enhanced Modal with Better UX */}
      {showModal && (
        <div 
          className="fixed inset-0 bg-black/95 backdrop-blur-sm flex items-center justify-center z-50"
          onClick={closeModal}
          onKeyDown={handleKeyDown}
          tabIndex={0}
        >
          <div 
            className="relative max-w-7xl max-h-full p-4 w-full"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Enhanced Close Button */}
            <button
              onClick={closeModal}
              className="absolute top-6 right-6 text-white hover:text-gray-300 z-20 bg-black/50 backdrop-blur-sm rounded-full w-12 h-12 flex items-center justify-center transition-all duration-300 hover:bg-black/70 hover:scale-110"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            {/* Main Image Container */}
            <div className="relative bg-black rounded-2xl overflow-hidden shadow-2xl">
              {/* Navigation Arrows */}
              {allImages.length > 1 && (
                <>
                  <button
                    onClick={prevImage}
                    className="absolute left-6 top-1/2 transform -translate-y-1/2 text-white bg-black/50 backdrop-blur-sm rounded-full w-14 h-14 flex items-center justify-center hover:bg-black/70 transition-all duration-300 z-10 hover:scale-110"
                  >
                    <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                  </button>
                  <button
                    onClick={nextImage}
                    className="absolute right-6 top-1/2 transform -translate-y-1/2 text-white bg-black/50 backdrop-blur-sm rounded-full w-14 h-14 flex items-center justify-center hover:bg-black/70 transition-all duration-300 z-10 hover:scale-110"
                  >
                    <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                </>
              )}

              <img
                src={allImages[currentImageIndex]?.imageUrl || 'https://via.placeholder.com/800x600'}
                alt={`Hotel Image ${currentImageIndex + 1}`}
                className="max-w-full max-h-[85vh] object-contain mx-auto block"
              />

              {/* Enhanced Image Info Bar */}
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 via-black/50 to-transparent p-6">
                <div className="flex items-center justify-between text-white">
                  <div>
                    <div className="text-lg font-semibold mb-1">
                      Hotel Gallery
                    </div>
                    <div className="text-sm opacity-90">
                      Image {currentImageIndex + 1} of {allImages.length}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-xs bg-white/20 backdrop-blur-sm px-3 py-1 rounded-full">
                      Use ← → keys to navigate
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Enhanced Thumbnail Strip */}
            {allImages.length > 1 && (
              <div className="flex justify-center mt-6 space-x-3 overflow-x-auto pb-2 max-w-full">
                <div className="flex space-x-3 px-4">
                  {allImages.map((image, index) => (
                    <img
                      key={index}
                      src={image.imageUrl || 'https://via.placeholder.com/100x75'}
                      alt={`Thumbnail ${index + 1}`}
                      className={`w-20 h-14 object-cover rounded-lg cursor-pointer transition-all duration-300 flex-shrink-0 ${
                        index === currentImageIndex 
                          ? 'ring-2 ring-white shadow-2xl scale-110 opacity-100' 
                          : 'opacity-60 hover:opacity-90 hover:scale-105'
                      }`}
                      onClick={() => setCurrentImageIndex(index)}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default PhotoGallery;